import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@groot/ui/card";
import { Button } from "@groot/ui/button";
import { Skeleton } from "@groot/ui/loading-skeleton";
import { Text, metaLabelClass, monoClass } from "@groot/ui/typography";
import { TrackingStatusBadge } from "@/components/status-badges";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Copy,
  ExternalLink,
  Inbox,
  PackageOpen,
  RotateCcw,
  RefreshCw,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import {
  TRACKING_STATE_LABELS,
  formatTrackingDate,
  timeAgo,
  useRefreshTracking,
  useTracking,
  type ShipmentTracking,
  type TrackingEvent,
} from "../hooks/useTracking";

/**
 * Shipment tracking card for the order page: live Speed Post status, journey
 * facts and the full event timeline recorded in our database.
 */

const MILESTONE_ICONS = {
  booked: Inbox,
  in_transit: Truck,
  out_for_delivery: PackageOpen,
  delivered: CheckCircle2,
  returning: RotateCcw,
  exception: AlertTriangle,
} as const;

const MILESTONE_ICON_TONE = {
  booked: "text-muted-foreground",
  in_transit: "text-info",
  out_for_delivery: "text-info",
  delivered: "text-success",
  returning: "text-destructive",
  exception: "text-destructive",
} as const;

const ARTICLE_TYPE_LABELS: Record<string, string> = {
  SP_INLAND_DOC: "Speed Post document",
  SP_INLAND_PARCEL: "Speed Post parcel",
};

function journeyLine(tracking: ShipmentTracking): { from: string; to: string } {
  const details = tracking.bookingDetails;
  const from = [details?.originCity, details?.originPincode].filter(Boolean).join(" ") || "—";
  const to =
    [details?.destinationCity, details?.destinationPincode].filter(Boolean).join(" ") || "—";
  return { from, to };
}

/** Vertical event timeline — newest first, shared with the public order page. */
export function TrackingTimeline({ events }: { events: TrackingEvent[] }) {
  if (events.length === 0) {
    return (
      <Text variant="meta" tone="muted">
        No scans recorded yet. India Post usually adds the first scan within a few hours of booking.
      </Text>
    );
  }
  return (
    <ol className="space-y-0">
      {events.map((event, index) => {
        const Icon = MILESTONE_ICONS[event.milestone] ?? Truck;
        const isLatest = index === 0;
        return (
          <li key={event.id} className="relative flex gap-3 pb-5 last:pb-0">
            {index < events.length - 1 && (
              <span
                className="absolute top-8 left-[13px] h-[calc(100%-28px)] w-px bg-border"
                aria-hidden
              />
            )}
            <span
              className={`relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full border bg-background ${
                isLatest ? "border-foreground/30" : "border-border"
              }`}
            >
              <Icon className={`size-3.5 ${MILESTONE_ICON_TONE[event.milestone] ?? ""}`} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <Text
                  variant="body"
                  className={isLatest ? "font-medium text-foreground" : "text-foreground"}
                >
                  {event.eventName}
                </Text>
                <Text variant="meta" tone="muted" className="shrink-0">
                  {formatTrackingDate(event.eventAt)}
                </Text>
              </div>
              {(event.office || event.remarks) && (
                <Text variant="meta" tone="muted" className="mt-0.5 block">
                  {[event.office, event.remarks].filter(Boolean).join(" · ")}
                </Text>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function TrackingCard({
  trackingNumber,
  orderId,
}: {
  trackingNumber: string;
  orderId?: number;
}) {
  const normalized = trackingNumber.trim().toUpperCase();
  const { data, isLoading, isError, error, refetch } = useTracking(normalized, orderId);
  const refresh = useRefreshTracking(normalized, orderId);
  const [justCopied, setJustCopied] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-4 w-full max-w-sm" />
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Shipment tracking</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div className="flex-1">
              <Text variant="body">
                {error instanceof Error ? error.message : "Tracking is unavailable right now."}
              </Text>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => refetch()}
                disabled={refresh.isPending}
              >
                <RefreshCw className="size-4" />
                Try again
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { tracking, events } = data;
  const { from, to } = journeyLine(tracking);
  const articleLabel = tracking.bookingDetails?.articleType
    ? (ARTICLE_TYPE_LABELS[tracking.bookingDetails.articleType] ??
      tracking.bookingDetails.articleType)
    : null;
  const isTerminal = tracking.state === "delivered" || tracking.state === "returned";

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(normalized);
      setJustCopied(true);
      toast.success("Tracking number copied");
      setTimeout(() => setJustCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>Shipment tracking</CardTitle>
        <div className="flex items-center gap-2">
          {!isTerminal && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              <RefreshCw className={`size-4 ${refresh.isPending ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          )}
          <Button variant="outline" size="sm" asChild>
            <a
              href={`https://speedposttrack.io/tracking-result?d=${encodeURIComponent(
                btoa(JSON.stringify({ t: normalized, c: "speedpost" })),
              )}`}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink className="size-4" />
              Open
            </a>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Identity + current status */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <TrackingStatusBadge
            state={tracking.state}
            label={TRACKING_STATE_LABELS[tracking.state] ?? tracking.state}
          />
          <button
            type="button"
            onClick={copyNumber}
            className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
            title="Copy tracking number"
          >
            <span className={monoClass}>{normalized}</span>
            <Copy className="size-3.5" />
            {justCopied && <span className="text-[11px] text-success">copied</span>}
          </button>
          {tracking.lastFetchAt && (
            <Text variant="meta" tone="muted">
              Updated {timeAgo(tracking.lastFetchAt)}
            </Text>
          )}
          {orderId && tracking.orderId !== null && tracking.orderId !== orderId && (
            <Text variant="meta" tone="muted">
              Shared with order #{tracking.orderId}
            </Text>
          )}
        </div>

        {(data.refreshError || tracking.lastError) && (
          <div className="flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
            <Text variant="meta" className="text-foreground">
              {data.refreshError ?? tracking.lastError} — showing the last saved update.
            </Text>
          </div>
        )}

        {tracking.state === "not_found" ? (
          <Text variant="body" tone="muted">
            No tracking details found for this number yet. India Post can take a few hours to
            register the first scan; try refreshing later.
          </Text>
        ) : (
          <>
            {/* Journey facts */}
            <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
              <div>
                <p className={metaLabelClass}>From</p>
                <Text variant="body" className="mt-1 block">
                  {from}
                </Text>
                {tracking.bookedAt && (
                  <Text variant="meta" tone="muted" className="mt-0.5 block">
                    Booked {formatTrackingDate(tracking.bookedAt)}
                  </Text>
                )}
              </div>
              <ArrowRight className="hidden size-4 text-muted-foreground sm:block" aria-hidden />
              <div className="sm:text-right">
                <p className={metaLabelClass}>To</p>
                <Text variant="body" className="mt-1 block">
                  {to}
                </Text>
                {tracking.deliveredAt && (
                  <Text variant="meta" tone="muted" className="mt-0.5 block">
                    Delivered {formatTrackingDate(tracking.deliveredAt)}
                  </Text>
                )}
              </div>
            </div>

            {(articleLabel || tracking.bookingDetails?.weightGrams) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 border-t pt-3">
                {articleLabel && (
                  <Text variant="meta" tone="muted">
                    {articleLabel}
                  </Text>
                )}
                {tracking.bookingDetails?.weightGrams && (
                  <Text variant="meta" tone="muted">
                    {tracking.bookingDetails.weightGrams} g
                  </Text>
                )}
                {tracking.bookingDetails?.tariff != null && tracking.bookingDetails.tariff > 0 && (
                  <Text variant="meta" tone="muted">
                    ₹{tracking.bookingDetails.tariff} postage
                  </Text>
                )}
              </div>
            )}

            <div className="border-t pt-4">
              <p className={metaLabelClass}>Tracking history</p>
              <div className="mt-3">
                <TrackingTimeline events={events} />
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
